import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  biometricsLoggedIn,
  biometricsOptInPage,
  logout,
  removeBiometricData,
  updateBiometricError,
  setBiometricInProgress,
} from "../store/slices/authSlice";
import { isBiometricAvailable, promptBiometric } from "../utils/biometric";
import {
  clearBiometricCredential,
  persistOptInBiometricCredential,
  fetchStoredBiometricCredential,
  validateBiometricCredential,
  ValidBiometricCredential,
} from "../utils/biometricSession";
import {
  BIOMETRIC_CREDENTIAL_SAVED_KEY,
  BiometricEnrollmentError,
} from "../constants";
import { UserData } from "../types/types";
import { performBiometricReauth } from "../utils/biometricReauth";
import { getPreference } from "../utils/preferences";
import dayjs from "../utils/dayjsConfig";
import { isOtaApplying } from "../utils/otaState";
import * as Sentry from "@sentry/react";
import {
  ENABLE_BIOMETRIC_LOGS,
  ENABLE_PROTECTED_ROUTE_TELEMETRY,
} from "../constants/logging";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const biometricLog = (...args: unknown[]) => {
  if (!ENABLE_BIOMETRIC_LOGS) return;
  // eslint-disable-next-line no-console
  console.log(...args);
};

// QA-tunable: increase to widen the retry window when testing the
// isBiometricAvailable transient-false race; lower to 0 to remove the gap.
const BIOMETRIC_AVAILABILITY_RETRY_DELAY_MS = 500;

export type UseBiometricAuthOptions = {
  quickLoginConditions?: boolean;
  isAppActive?: boolean;
  onFinishOptInFlow?: () => void;
};

export const useBiometricAuth = ({
  quickLoginConditions,
  isAppActive,
  onFinishOptInFlow,
}: UseBiometricAuthOptions = {}) => {
  const dispatch = useAppDispatch();
  const { biometrics, auth, user } = useAppSelector(
    (state) => state.authentication,
  );
  const { isInForeground } = useAppSelector((state) => state.appState);

  const isNative = Capacitor.isNativePlatform();

  const [quickLoginInProgress, setQuickLoginInProgress] = useState(false);
  const [isDeviceBiometricAvailable, setIsDeviceBiometricAvailable] = useState(false);

  const hasAttemptedBiometricLogin = useRef(false);

  // Ref to indicate we've waited for the biometric quick login check
  const [waitForBiometricQuickLoginCheck, setWaitForBiometricQuickLoginCheck] =
    useState(false);

  useEffect(() => {
    if (!isNative) return;
    isBiometricAvailable().then((res) => {
      setIsDeviceBiometricAvailable(res.isAvailable);
    });
  }, [isNative]);

  const shouldAttemptQuickLogin = async () => {
    if (
      !isNative ||
      !quickLoginConditions ||
      !isAppActive ||
      !isInForeground ||
      isOtaApplying() ||
      hasAttemptedBiometricLogin.current ||
      biometrics?.error ||
      biometrics?.inProgress
    ) {
      return false;
    }
    try {
      const preference = await getPreference(BIOMETRIC_CREDENTIAL_SAVED_KEY);

      return preference === "true";
    } catch (error) {
      return false;
    }
  };

  const quickLoginWithBiometrics = useCallback(async () => {
    if (
      !isNative ||
      !quickLoginConditions ||
      !isAppActive ||
      !isInForeground ||
      isOtaApplying() ||
      hasAttemptedBiometricLogin.current ||
      biometrics?.error ||
      biometrics?.inProgress
    ) {
      // Only flip pending to false on the first attempt; avoid racing when a second call hits the guard
      if (!hasAttemptedBiometricLogin.current) {
        setQuickLoginInProgress(false);
        dispatch(setBiometricInProgress(false));
      }
      return;
    }

    setQuickLoginInProgress(true);

    // Mark that we've attempted biometric login to avoid repeated prompts
    hasAttemptedBiometricLogin.current = true;
    dispatch(setBiometricInProgress(true));

    // Ensure we're on a native platform
    if (!Capacitor.isNativePlatform()) {
      setQuickLoginInProgress(false);
      dispatch(setBiometricInProgress(false));
      return;
    }

    // If the user is already logged in, skip biometric login
    if (user || auth?.access_token) {
      setQuickLoginInProgress(false);
      dispatch(setBiometricInProgress(false));
      return;
    }

    // Check if biometric authentication is available
    const availability = await isBiometricAvailable();
    if (!availability?.isAvailable) {
      setQuickLoginInProgress(false);
      dispatch(setBiometricInProgress(false));
      return;
    }

    const secureSession = await fetchStoredBiometricCredential(
      "use-biometric-auth:quick-login",
    );
    const validation = validateBiometricCredential(secureSession);

    if (!validation.valid) {
      if (validation.expired) {
        await clearBiometricCredential();
      }

      setQuickLoginInProgress(false);
      dispatch(setBiometricInProgress(false));
      return;
    }

    const reauthResult = await performBiometricReauth({
      dispatch,
      credential: validation.credential as ValidBiometricCredential,
      userEmail: (user as UserData | null)?.email ?? null,
      recomputeServerDiff: true,
    });

    if (!reauthResult.success) {
      // Capture telemetry before branching on step so errorCode/nativeMessage
      // (only populated when step === "prompt") are preserved. Matches the
      // pattern used in ProtectedRoute.tsx.
      if (ENABLE_PROTECTED_ROUTE_TELEMETRY) {
        Sentry.withScope((scope) => {
          scope.setTag("module", "useBiometricAuth");
          scope.setTag("logout_trigger", "quick_login_reauth_failed");
          scope.setTag("platform", Capacitor.getPlatform());
          scope.setTag("isNative", String(Capacitor.isNativePlatform()));
          scope.setExtra("step", reauthResult.step);
          scope.setExtra("error", reauthResult.error);
          scope.setExtra("errorCode", reauthResult.errorCode);
          scope.setExtra("nativeMessage", reauthResult.nativeMessage);
          scope.setExtra("currentTime", new Date().toISOString());
          Sentry.captureMessage(
            "biometricAuth.quick_login_reauth_failed",
            "warning",
          );
        });
      }

      if (reauthResult.step === "prompt") {
        dispatch(
          updateBiometricError(reauthResult.error as BiometricEnrollmentError),
        );
      } else {
        dispatch(removeBiometricData());
        dispatch(logout());
      }
      setQuickLoginInProgress(false);
      dispatch(setBiometricInProgress(false));
      return;
    }

    setQuickLoginInProgress(false);
  }, [
    auth?.access_token,
    biometrics?.error,
    dispatch,
    isNative,
    isAppActive,
    quickLoginConditions,
    user,
  ]);

  // should attempt quick login
  useEffect(() => {
    const attemptQuickLogin = async () => {
      try {
        const shouldAttempt = await shouldAttemptQuickLogin();

        if (shouldAttempt) {
          quickLoginWithBiometrics();
        }
      } finally {
        setWaitForBiometricQuickLoginCheck(true);
      }
    };

    attemptQuickLogin();
  }, [
    isNative,
    quickLoginConditions,
    isAppActive,
    isInForeground,
    biometrics?.error,
    biometrics?.inProgress,
    quickLoginWithBiometrics,
  ]);

  const startBiometricOptInFlow = useCallback(async () => {
    biometricLog("[BIOMETRIC] startOptIn: enter", {
      isAppActive,
      hasError: Boolean(biometrics?.error),
      currentInProgress: Boolean(biometrics?.inProgress),
      currentOptInPage: biometrics?.optInPage ?? null,
      currentLoggedIn: Boolean(biometrics?.loggedIn),
    });

    if (!isAppActive) {
      biometricLog("[BIOMETRIC] startOptIn: bail, !isAppActive");
      return;
    }

    if (biometrics?.error) {
      biometricLog("[BIOMETRIC] startOptIn: bail, biometrics.error", {
        error: biometrics.error,
      });
      return;
    }

    dispatch(setBiometricInProgress(true));
    biometricLog("[BIOMETRIC] startOptIn: dispatched inProgress=true");

    let availability = await isBiometricAvailable();
    biometricLog("[BIOMETRIC] startOptIn: isAvailable result (attempt 1)", {
      availability,
      isNative: Capacitor.isNativePlatform(),
      biometricsLoggedIn: Boolean(biometrics?.loggedIn),
      biometricsOptInPage: biometrics?.optInPage ?? null,
    });

    // Retry once after a short delay. The native plugin can transiently
    // report isAvailable=false on cold start (e.g. after device restart,
    // before iOS finishes biometric subsystem init). Without this retry the
    // UI loops between IN_PROGRESS and NEEDS_OPT_IN forever via
    // onFinishOptInFlow.
    if (!availability?.isAvailable) {
      await delay(BIOMETRIC_AVAILABILITY_RETRY_DELAY_MS);
      availability = await isBiometricAvailable();
      biometricLog(
        "[BIOMETRIC] startOptIn: isAvailable result (attempt 2 retry)",
        {
          availability,
        },
      );
      try {
        Sentry.addBreadcrumb({
          category: "auth.biometric",
          message: "isBiometricAvailable retried after first false",
          data: { secondAttemptResult: availability },
          level: "info",
        });
      } catch {
        // Sentry must not block.
      }
    }

    if (
      Capacitor.isNativePlatform() &&
      availability?.isAvailable &&
      !biometrics?.loggedIn
    ) {
      biometricLog("[BIOMETRIC] startOptIn: branch=available", {
        willDispatchLegalInfo: biometrics?.optInPage === null,
      });
      if (biometrics?.optInPage === null) {
        dispatch(biometricsOptInPage("LEGAL_INFO"));
      }

      return;
    }

    // If rejected or not available after retry, run onFinishOptInFlow
    // callback immediately. The caller (LoginPage) decides what "finish"
    // means — current implementation navigates to home, breaking the loop
    // structurally.
    biometricLog("[BIOMETRIC] startOptIn: branch=rejected/unavailable", {
      isNative: Capacitor.isNativePlatform(),
      isAvailable: availability?.isAvailable ?? null,
      loggedIn: Boolean(biometrics?.loggedIn),
    });
    try {
      Sentry.captureMessage("biometric_opt_in_unavailable_after_retry", {
        level: "warning",
        tags: { module: "useBiometricAuth" },
        extra: {
          availability,
          isNative: Capacitor.isNativePlatform(),
          loggedIn: Boolean(biometrics?.loggedIn),
        },
      });
    } catch {
      // Sentry must not block.
    }
    dispatch(setBiometricInProgress(false));
    biometricLog(
      "[BIOMETRIC] startOptIn: dispatched inProgress=false, calling onFinishOptInFlow",
    );
    onFinishOptInFlow?.();
  }, [
    biometrics?.error,
    biometrics?.loggedIn,
    biometrics?.optInPage,
    dispatch,
    isAppActive,
    onFinishOptInFlow,
  ]);

  const handleBiometricAccept = useCallback(async () => {
    if (!user || !auth) {
      throw new Error("User or auth data is missing");
    }

    dispatch(setBiometricInProgress(true));
    dispatch(biometricsOptInPage("ACCEPTED_BIOMETRICS"));
    await delay(1200);
    dispatch(biometricsOptInPage(null));

    try {
      await promptBiometric();
      await clearBiometricCredential();
      if (!auth.refresh_token || !auth.expires_at || !auth.execute_at) {
        throw new Error("Missing refresh token metadata to persist");
      }

      const serverDiffMs = dayjs()
        .utc()
        .diff(dayjs.utc(auth.execute_at), "millisecond");

      await persistOptInBiometricCredential({
        username: user.email,
        refreshToken: auth.refresh_token,
        serverDiffMs,
        executeAt: auth.execute_at,
        state: auth.state ?? null,
      });

      dispatch(biometricsLoggedIn(true));
      dispatch(setBiometricInProgress(false));
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);

      dispatch(updateBiometricError(errorMessage as BiometricEnrollmentError));
      setQuickLoginInProgress(false);
      dispatch(setBiometricInProgress(false));
    }
  }, [auth, dispatch, user]);

  // On successful biometric login, call onFinishOptInFlow callback
  useEffect(() => {
    if (biometrics?.loggedIn && user && isAppActive) {
      onFinishOptInFlow?.();
    }
  }, [biometrics?.loggedIn, isAppActive, onFinishOptInFlow, user]);

  useEffect(() => {
    if (!isAppActive) {
      dispatch(setBiometricInProgress(false));
      // Keep quickLoginInProgress true while backgrounded so slower devices
      // don't fall back to external auth mid-biometric quick login
    }
  }, [isAppActive]);

  return {
    biometrics,
    quickLoginInProgress,
    optInInProgress: biometrics?.inProgress,
    startBiometricOptInFlow,
    handleBiometricAccept,
    waitForBiometricQuickLoginCheck,
    isDeviceBiometricAvailable,
  };
};
